import { DeploymentRing } from '@fluidx/office-fluid-types';
import { getFluidDevOverrides, getCdnBaseUrl, scheduleIdleTask } from '@fluidx/utilities';
import * as devOverrideKeys from './devOverrideKeys';
import {
  getCachedManifestEntryForDeploymentRing,
  cacheManifestEntryForDeploymentRing,
  isManifestCachingMechanismEnabled
} from './ManifestCacheHelpers';
import { OdspDriverManifest, ManifestUrlSuffixRelativeToBaseCdnPath } from './OdspDriverManifestType';

// Cache the manifest for up to a week
const maxCacheAgeMs = 7 * 24 * 60 * 60 * 1000;

export type OdspDriverManifestCacheEntry = OdspDriverManifest & { lastUpdatedTime: number };

/**
 * Gets the driver manifest, either from the cache (if a cache entry exists and hasn't expired),
 * or by fetching it.
 * If returning from the cache, this method will also schedule a task
 * to do a best-effort re-fetching of the manifest, to store into the cache later.
 * @param environment - The CDN environment to fetch from (and/or to use as the caching key)
 */
export async function getOrFetchOdspDriverManifest(environment?: DeploymentRing): Promise<OdspDriverManifest> {
  const cachedEntry = getCachedOdspDriverManifest(environment);
  if (cachedEntry) {
    // Before returning the entry, queue a best-effort to re-fetch it:
    queueBestEffortToUpdateOdspDriverManifestOnIdle(environment);
    return cachedEntry;
  }

  return await fetchOdspDriverManifest(environment);
}

/**
 * Retrieves the cached ODSP driver manifest if available and not expired. Otherwise returns null.
 * @param environment - The CDN environment (to use as the caching key)
 */
export function getCachedOdspDriverManifest(environment?: DeploymentRing): OdspDriverManifest | null {
  try {
    const entry = getCachedManifestEntryForDeploymentRing(environment);
    if (!entry) {
      return null;
    }

    const cacheEntryAge = Date.now() - entry.lastUpdatedTime;

    if (cacheEntryAge > maxCacheAgeMs) {
      return null;
    }

    return entry;
  } catch (e: any) {
    return null;
  }
}

export function queueBestEffortToUpdateOdspDriverManifestOnIdle(environment?: DeploymentRing): void {
  isManifestCachingMechanismEnabled() && scheduleIdleTask(() => fetchOdspDriverManifest(environment).catch((_e) => {}));
}

/**
 * Fetches the ODSP driver manifest based on the specified environment
 * (unless a localStorage manifest-url override specifies).
 * Upon fetching the manifest, also does a best-effort to store it in the cache
 * @param environment - The CDN environment to fetch the driver from
 */
export async function fetchOdspDriverManifest(environment?: DeploymentRing): Promise<OdspDriverManifest> {
  const localStorageOverrideForManifestUrl = getFluidDevOverrides(devOverrideKeys.odspDriverManifestUrl);
  const manifestUrl =
    localStorageOverrideForManifestUrl || getCdnBaseUrl(environment) + ManifestUrlSuffixRelativeToBaseCdnPath;
  const response = await fetch(manifestUrl, { method: 'GET' });
  if (response.ok) {
    const manifest: OdspDriverManifest = await response.json();

    // Before returning, do a best-effort to store the manifest in the cache
    cacheManifestEntryForDeploymentRing(manifest, environment);

    return manifest;
  }

  throw new Error('Error fetching ODSP Driver manifest');
}

/**
 * Checks whether ODSP driver caching is enabled
 **/
export function isOdspDriverCachingMechanismEnabled(): boolean {
  return isManifestCachingMechanismEnabled();
}
